import { useState, useCallback, useRef, useEffect, memo } from 'react';
import { createPortal } from 'react-dom';
import { IoEyeOutline, IoCloseOutline } from 'react-icons/io5';
import BestStartButton from '../button';
import { useDebouncedCallback } from 'use-debounce';
import Input from '../input';
import api from '../../services/api';
import './index.css';

function useDropdownPosition(inputRef, open) {
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0, maxHeight: 240 });

  useEffect(() => {
    if (!open || !inputRef.current) return;
    function update() {
      const rect = inputRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const openUp = spaceBelow < 240 && spaceAbove > spaceBelow;
      const maxHeight = Math.max(120, Math.min(240, (openUp ? spaceAbove : spaceBelow) - 12));
      setPos({
        left: rect.left,
        width: rect.width,
        top: openUp ? undefined : rect.bottom + 4,
        bottom: openUp ? window.innerHeight - rect.top + 4 : undefined,
        maxHeight,
      });
    }
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [inputRef, open]);

  return pos;
}

function PersonAvatar({ id, initials }) {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) { setLoading(false); return; }
    let active = true;
    let objectUrl;
    const controller = new AbortController();

    setLoading(true);
    api.getEntraStaffPhoto(id, controller.signal)
      .then((blob) => {
        if (!active || !(blob instanceof Blob) || blob.size === 0) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => {})
      .finally(() => { if (active) setLoading(false); });

    return () => {
      active = false;
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [id]);

  return (
    <span className="person-search-avatar" aria-hidden="true">
      {loading
        ? <span className="person-search-avatar-spinner" />
        : url
          ? <img className="person-search-avatar-img" src={url} alt="" />
          : initials}
    </span>
  );
}

const formatRoleDate = (value) => {
  if (!value) return null;
  const parsed = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

const parseRoleDate = (value) => {
  if (!value) return null;
  const parsed = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const getRolePhase = (role) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const starts = parseRoleDate(role.effectiveDate);
  const ends = parseRoleDate(role.ineffectiveDate);
  if (starts && starts > today) return 'upcoming';
  if (ends && ends < today) return 'previous';
  return 'current';
};

const RoleItem = memo(function RoleItem({ role }) {
  const phase = getRolePhase(role);
  const from = formatRoleDate(role.effectiveDate);
  const to = formatRoleDate(role.ineffectiveDate);
  return (
    <li className="roles-drawer-item">
      <span className="roles-drawer-swatch" style={{ background: role.roleRgb || '#999' }} aria-hidden="true" />
      <span className="roles-drawer-item-body">
        <span className="roles-drawer-parent">{role.parentName}</span>
        <span className="roles-drawer-role" style={{ color: role.roleRgb || undefined }}>{role.roleName}</span>
        <span className="roles-drawer-dates">
          {phase === 'upcoming'
            ? `Starting: ${from ?? '—'}`
            : <>{from ?? '—'}<span aria-hidden="true"> → </span>{to ?? 'Present'}</>}
        </span>
      </span>
    </li>
  );
});

function RolesDrawer({ person, onClose }) {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const name = `${person.givenName ?? ''} ${person.surname ?? ''}`.trim() || person.displayName || '?';

  const currentRoles = roles.filter((r) => getRolePhase(r) === 'current');
  const upcomingRoles = roles.filter((r) => getRolePhase(r) === 'upcoming');
  const previousRoles = roles.filter((r) => getRolePhase(r) === 'previous');

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setLoading(true);
    setError(false);
    api.getStaffRoleList(person.id, controller.signal)
      .then((data) => {
        if (!active) return;
        setRoles(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        if (!active || err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return;
        setError(true);
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; controller.abort(); };
  }, [person.id]);

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div className="roles-drawer-overlay" onMouseDown={onClose}>
      <aside
        className="roles-drawer"
        role="dialog"
        aria-label={`Roles for ${name}`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="roles-drawer-header">
          <span className="roles-drawer-title">{name}</span>
          <BestStartButton className="roles-drawer-close" onClick={onClose} aria-label="Close" content={<IoCloseOutline />} color="red" width={44} height={44} />
        </header>
        <div className="roles-drawer-body">
          {loading && <div className="roles-drawer-status"><span className="person-search-avatar-spinner" /> Loading roles…</div>}
          {!loading && error && <div className="roles-drawer-status">Could not load roles.</div>}
          {!loading && !error && roles.length === 0 && <div className="roles-drawer-status">No roles found.</div>}
          {!loading && !error && roles.length > 0 && (
            <>
              <section className="roles-drawer-section">
                <h4 className="roles-drawer-section-title">Current</h4>
                {currentRoles.length === 0
                  ? <div className="roles-drawer-status">No current roles.</div>
                  : (
                    <ul className="roles-drawer-list">
                      {currentRoles.map((r, i) => <RoleItem key={`${r.parentId}-${r.roleId}-${i}`} role={r} />)}
                    </ul>
                  )}
              </section>
              {upcomingRoles.length > 0 && (
                <section className="roles-drawer-section">
                  <h4 className="roles-drawer-section-title">Upcoming</h4>
                  <ul className="roles-drawer-list">
                    {upcomingRoles.map((r, i) => <RoleItem key={`${r.parentId}-${r.roleId}-${i}`} role={r} />)}
                  </ul>
                </section>
              )}
              {previousRoles.length > 0 && (
                <section className="roles-drawer-section">
                  <h4 className="roles-drawer-section-title">Previous</h4>
                  <ul className="roles-drawer-list">
                    {previousRoles.map((r, i) => <RoleItem key={`${r.parentId}-${r.roleId}-${i}`} role={r} />)}
                  </ul>
                </section>
              )}
            </>
          )}
        </div>
      </aside>
    </div>,
    document.body,
  );
}

export default function PersonSearch({ onSelect, disabled, placeholder }) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rolesPerson, setRolesPerson] = useState(null);
  const abortRef = useRef(null);
  const containerRef = useRef(null);
  const inputWrapRef = useRef(null);
  const pos = useDropdownPosition(inputWrapRef, open);

  const fetchSuggestions = useDebouncedCallback(async (text) => {
    if (!text || text.length < 2) {
      setSuggestions([]);
      setOpen(false);
      return;
    }

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    try {
      const data = await api.searchEntraStaff(text, controller.signal);
      if (controller.signal.aborted) return;
      const results = Array.isArray(data) ? data : [];
      setSuggestions(results);
      setOpen(results.length > 0);
    } catch (err) {
      if (err?.name !== 'AbortError' && err?.code !== 'ERR_CANCELED') {
        setSuggestions([]);
        setOpen(false);
      }
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, 400, []);

  const handleChange = useCallback((text) => {
    setQuery(text);
    fetchSuggestions(text);
  }, [fetchSuggestions]);

  const handleSelect = useCallback((person) => {
    setSuggestions([]);
    setOpen(false);
    setQuery(`${person.givenName ?? ''} ${person.surname ?? ''}`.trim() || person.displayName || '');
    onSelect?.(person);
  }, [onSelect]);

  const handleReopen = useCallback(() => {
    if (suggestions.length > 0) {
      setOpen(true);
    } else if (query.trim().length >= 2) {
      fetchSuggestions(query.trim());
    }
  }, [suggestions, query, fetchSuggestions]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (e.target?.closest?.('.roles-drawer-overlay')) return;
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="person-search" ref={containerRef}>
      <div className="person-search-input-wrap" ref={inputWrapRef}>
        <Input
          value={query}
          onChange={handleChange}
          onFocus={handleReopen}
          onClick={handleReopen}
          placeholder={placeholder ?? 'Search by name or email…'}
          autoComplete="new-password"
          name="person-search-no-autofill"
          disabled={disabled}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={open}
          data-lpignore="true"
          data-form-type="other"
          data-1p-ignore="true"
          spellCheck={false}
          autoCorrect="off"
          autoCapitalize="off"
        />
        {loading && <span className="person-search-spinner" aria-label="Searching…" />}
      </div>
      {open && suggestions.length > 0 && createPortal(
        <ul
          className="person-search-dropdown"
          role="listbox"
          style={{
            position: 'fixed',
            top: pos.top,
            bottom: pos.bottom,
            left: pos.left,
            width: pos.width,
            maxHeight: pos.maxHeight,
          }}
        >
          {suggestions.map((person) => {
            const name = `${person.givenName ?? ''} ${person.surname ?? ''}`.trim() || person.displayName || '?';
            const initials = `${person.givenName?.[0] ?? ''}${person.surname?.[0] ?? ''}`.toUpperCase() || name[0]?.toUpperCase() || '?';
            const role = [person.jobTitle, person.department].filter(Boolean).join(' · ');
            return (
              <li
                key={person.id}
                className="person-search-option"
                role="option"
                onMouseDown={(e) => { e.preventDefault(); handleSelect(person); }}
              >
                <PersonAvatar id={person.id} initials={initials} />
                <span className="person-search-body">
                  <span className="person-search-name">{name}</span>
                  {role && <span className="person-search-role">{role}</span>}
                  {person.mail && <span className="person-search-email">{person.mail}</span>}
                  {person.employeeId && <span className="person-search-employee-id">ID: {person.employeeId}</span>}
                </span>
                <BestStartButton
                  type="button"
                  width={42}
                  height={42}
                  tooltip="View roles"
                  className="person-search-eye"
                  aria-label={`View roles for ${name}`}
                  onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setRolesPerson(person); }}
                  content={<IoEyeOutline size={18} aria-hidden="true" />}
                >
                </BestStartButton>
              </li>
            );
          })}
        </ul>,
        document.body,
      )}
      {rolesPerson && (
        <RolesDrawer person={rolesPerson} onClose={() => setRolesPerson(null)} />
      )}
    </div>
  );
}
